import React, { useState, useEffect, useCallback } from "react";
import { BarChart3, Clock, CheckCircle, Loader } from "lucide-react";
import theme from "../../styles/theme";
import { fetchSurveys } from "../../api";
import SurveyCard from "./SurveyCard";

const T = theme.colors;

export default function SurveyTab() {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, active, closed

  useEffect(() => {
    setLoading(true);
    fetchSurveys()
      .then((data) => setSurveys(data || []))
      .catch((err) => console.error("Failed to load surveys:", err))
      .finally(() => setLoading(false));
  }, []);

  const handleSurveyUpdate = useCallback((updatedSurvey) => {
    setSurveys((prev) =>
      prev.map((s) => (s.id === updatedSurvey.id ? updatedSurvey : s))
    );
  }, []);

  const filtered = surveys.filter((s) => {
    if (filter === "active") return s.status === "active" && !s.isExpired;
    if (filter === "closed") return s.status === "closed" || s.isExpired;
    return true;
  });

  const activeCount = surveys.filter((s) => s.status === "active" && !s.isExpired).length;
  const closedCount = surveys.filter((s) => s.status === "closed" || s.isExpired).length;

  return (
    <div>
      {/* Header */}
      <div style={{
        marginBottom: 24,
        padding: "24px 0 0",
      }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          marginBottom: 8,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: T.purple + "20",
            border: `1px solid ${T.purple}40`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <BarChart3 size={18} color={T.violet} />
          </div>
          <div>
            <h2 style={{
              fontSize: 20, fontWeight: 700, color: "#fff",
              margin: 0, lineHeight: 1.2,
            }}>
              Community Surveys
            </h2>
            <p style={{
              fontSize: 12, color: T.textDim, margin: 0,
              fontFamily: theme.fonts.mono,
            }}>
              Your voice shapes our next analysis
            </p>
          </div>
        </div>

        {/* Filter tabs */}
        <div style={{
          display: "flex", gap: 6, marginTop: 16,
        }}>
          {[
            { key: "all", label: "All", count: surveys.length },
            { key: "active", label: "Active", count: activeCount, icon: Clock, color: "#4ade80" },
            { key: "closed", label: "Results", count: closedCount, icon: CheckCircle, color: T.textDim },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                style={{
                  display: "flex", alignItems: "center", gap: 5,
                  padding: "6px 14px", borderRadius: 20,
                  border: `1px solid ${filter === tab.key ? T.purple + "60" : T.border}`,
                  background: filter === tab.key ? T.purple + "15" : "transparent",
                  color: filter === tab.key ? T.violet : T.textMuted,
                  fontSize: 12, cursor: "pointer",
                  fontFamily: theme.fonts.mono,
                  transition: "all 0.2s",
                }}
              >
                {Icon && <Icon size={12} />}
                {tab.label}
                <span style={{
                  fontSize: 10, padding: "1px 5px", borderRadius: 8,
                  background: filter === tab.key ? T.purple + "25" : T.surface,
                  color: filter === tab.key ? T.violet : T.textDim,
                }}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Loader
            size={24} color={T.violet}
            style={{ animation: "spin 1s linear infinite" }}
          />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Survey list */}
      {!loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {filtered.length === 0 && (
            <div style={{
              textAlign: "center", padding: 48,
              color: T.textDim, fontSize: 14,
            }}>
              {filter === "active"
                ? "No active surveys right now. Check back soon!"
                : filter === "closed"
                ? "No closed surveys with results yet."
                : "No surveys available yet."
              }
            </div>
          )}

          {filtered.map((survey) => (
            <SurveyCard
              key={survey.id}
              survey={survey}
              onUpdate={handleSurveyUpdate}
            />
          ))}
        </div>
      )}

      {/* Info note */}
      {!loading && surveys.length > 0 && (
        <div style={{
          marginTop: 24, padding: "14px 18px",
          borderRadius: 10,
          background: T.surface + "60",
          border: `1px solid ${T.border}`,
          fontSize: 12, color: T.textDim, lineHeight: 1.6,
          textAlign: "center",
        }}>
          Surveys are anonymous — your vote is tied to your device fingerprint, not your identity.
          New analyses are published every 3 days based on community voting.
        </div>
      )}
    </div>
  );
}
